import { useContext, useEffect, useState } from "react";
import { ListPageProps } from "./Index";
import { Operation, TableHeaders } from "../../../../../utils/Enum.types";
import { Title } from "../../../../../stories/Title/Title";
import { TableHeaderProps } from "../../../../../utils/types";
import {
  HEADER_TEXT_ALIGN,
  SLNO_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../styles/Variables";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro";
import Edit from "../../../../../images/EditProfile.svg";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import DeleteModal from "../../../../../customComponents/DeleteModal";
import { GetDeviceTypes, ReOrderDeviceTypes } from "./queries/queries";
import {
  AtsDeviceTypes,
  DeleteDeviceTypeByIdResult,
  DeleteDeviceTypeByIdVars,
  GetDeviceTypesResult,
  GetDeviceTypesVars,
  ReOrderDeviceTypesResult,
  ReOrderDeviceTypesVar,
} from "./queries/types";
import { useLazyQuery, useMutation } from "@apollo/client";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { AppContext } from "../../../../../context/context";
import { payloadTypes } from "../../../../../context/reducer";
import { DeleteDeviceType } from "./queries/mutations";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import Delete from "../../../../../images/Delete.svg";
import LoadingModal from "../../../../pages/LoadingModal";
import Reorder from "../../../../../images/ReOrder.svg";

interface DraggedItem {
  item: AtsDeviceTypes;
  index: number;
}

const List = ({ setOperation, setMessage }: ListPageProps) => {
  const { customer_id } = useUrlParams();
  const { state, dispatch } = useContext(AppContext);
  const { user_details } = useLoggedInUserDetails();
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const groupInput = document.getElementsByName("groupName");
  const [reorder, setReorder] = useState(false);
  const [localItems, setLocalItems] = useState<AtsDeviceTypes[]>([]);
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const [ReOrder, { loading: reorderLoading }] = useMutation<
    ReOrderDeviceTypesResult,
    ReOrderDeviceTypesVar
  >(ReOrderDeviceTypes, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [getDeviceTypes, { data, loading }] = useLazyQuery<
    GetDeviceTypesResult,
    GetDeviceTypesVars
  >(GetDeviceTypes);

  const [deleteDeviceType, { loading: deleteLoading }] = useMutation<
    DeleteDeviceTypeByIdResult,
    DeleteDeviceTypeByIdVars
  >(DeleteDeviceType, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const dynamicHeaders: TableHeaderProps[] = [
    {
      headerName: "Sl",
      field: "id",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
    },
    {
      headerName: `Device Type Description`,
      field: "item_desc",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: 1,
      hideable: false,
    },
  ];
  const columns: GridColDef[] = [
    ...dynamicHeaders,
    {
      headerName: TableHeaders.ACTION,
      field: "actions",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      renderCell: (params) => {
        return (
          <>
            <img
              src={Edit}
              alt=""
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_DEVICE_TYPE_ID,
                  payload: {
                    deviceTypeID: params.row.cat_id,
                  },
                });
                if (groupInput.length) groupInput[0].focus();
                setOperation(Operation.UPDATE);
              }}
            />
            <img
              src={Delete}
              alt=""
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_DEVICE_TYPE_ID,
                  payload: {
                    deviceTypeID: params.row.cat_id,
                  },
                });
                setDeleteModal(!deleteModal);
              }}
            />
          </>
        );
      },
    },
  ];

  const HandleDelete = async () => {
    try {
      setDeleteModal(!deleteModal);
      deleteDeviceType({
        variables: {
          deviceTypeID: state.deviceTypeID,
          user_details,
        },
        refetchQueries: [
          {
            query: GetDeviceTypes,
            variables: {
              customerID: customer_id,
            },
          },
        ],
      }).then(({ data }) => {
        if (data) {
          setMessage({
            flag: true,
            message: "Device Type Deleted Sucessfully",
            operation: Operation.DELETE,
          });
        }
      });
    } catch (err) {
      console.error((err as Error).message);
    }
  };

  useEffect(() => {
    if (customer_id) {
      getDeviceTypes({
        variables: {
          customerID: customer_id,
        },
      }).then(({ data }) => {
        if (data && data.GetDeviceTypes) {
          setLocalItems(data.GetDeviceTypes);
        }
      });
    }
  }, [customer_id, getDeviceTypes]);

  useEffect(() => {
    if (data && data.GetDeviceTypes)
      setRows(
        data.GetDeviceTypes.map((node, index) => ({
          id: index + 1,
          item_desc: node.deviceType,
          cat_id: node.id,
        }))
      );
  }, [data]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    item: AtsDeviceTypes,
    index: number
  ): void => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number
  ): void => {
    e.preventDefault();

    if (draggedItem && draggedItem.index !== dropIndex) {
      const newItems: AtsDeviceTypes[] = [...localItems];
      const draggedItemData: AtsDeviceTypes = newItems[draggedItem.index];

      // Remove the dragged item
      newItems.splice(draggedItem.index, 1);

      // Insert at new position
      newItems.splice(dropIndex, 0, draggedItemData);

      setLocalItems(newItems);
    }

    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = (): void => {
    setDragOverIndex(null);
  };

  const handleSaveOrder = () => {
    if (customer_id) {
      ReOrder({
        variables: {
          customer_id,
          user_details,
          input: localItems.map((res, index) => ({
            deviceTypeID: res.id,
            idx: index + 1,
          })),
        },
        refetchQueries: [
          {
            query: GetDeviceTypes,
            variables: {
              customerID: customer_id,
            },
          },
        ],
      }).then(({ data }) => {
        if (data) {
          setMessage({
            flag: true,
            message: "Re-Order Successful",
            operation: Operation.UPDATE,
          });
          setReorder(false);
        }
      });
    }
  };

  return (
    <>
      <div className="master-data__list">
        <Title variant="subtitle1">{`List of Device Type`}</Title>
        <div className="master-data__list--tableblock">
          {data && !loading ? (
            reorder ? (
              localItems.map((label, index) => {
                return (
                  <div
                    className="add-wp-ts__list--table--li"
                    draggable
                    onDragStart={(e) => handleDragStart(e, label, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <label>{label.deviceType}</label>
                  </div>
                );
              })
            ) : (
              <StyledDatagrid
                columns={columns}
                rows={rows}
                rowHeight={TABLE_ROW_HEIGHT}
                disableRowSelectionOnClick
                hideFooter
              />
            )
          ) : null}
        </div>
        {reorder ? (
          <button
            className="add-wp-ts__list--re-order"
            disabled={reorderLoading}
            onClick={handleSaveOrder}
            type="button"
          >
            Save-order <img src={Reorder} alt="" />
          </button>
        ) : localItems.length > 1 ? (
          <button
            className="add-wp-ts__list--re-order"
            disabled={reorderLoading}
            onClick={() => setReorder(!reorder)}
            type="button"
          >
            Re-order <img src={Reorder} alt="" />
          </button>
        ) : (
          <></>
        )}
      </div>
      <DeleteModal
        id={state.deviceTypeID}
        modalFlag={deleteModal}
        setModalFlag={setDeleteModal}
        handleDelete={HandleDelete}
      />
      <LoadingModal flag={deleteLoading || loading || reorderLoading} />
    </>
  );
};

export default List;
